/**
 * Takes an object with objects as values and returns a new object with the same shape, where each value has
 * been serialized into a JSON string.
 *
 * @param obj The object to serialize values from.
 * @param space Same as the `space` argument of `JSON.stringify`. Defaults to `2`.
 * @returns A new object with values converted to a JSON string (or `undefined` if `obj` is falsy).
 */
export function prettyJsonValues(obj: Record<string, object>, space?: string | number): Record<string, string> {
  const entries = Object.entries(obj).map(([key, value]) => [key, JSON.stringify(value, null, space ?? 2)])
  return Object.fromEntries(entries)
}

/**
 * Checks whether or not `value` is `null`, `undefined` or empty.
 *
 * @param value The value to check.
 * @returns `true` if `value` is either nil or empty.
 */
export function isNilOrEmpty(value?: ArrayLike<unknown>) {
  return value === null || value === undefined || value.length === 0
}

/**
 * Parse yes/no like values.
 *
 * @param value The value to parse.
 * @param defaultValue The default value to return if no match was determined.
 * @returns The value coerced to a `boolean`.
 */
export function boolify(value?: any, defaultValue = false): boolean {
  if (isNilOrEmpty(value)) {
    return defaultValue
  }

  const stringValue = String(value).trim()

  if (/^(?:y|yes|true|1|on)$/i.test(stringValue)) {
    return true
  }

  if (/^(?:n|no|false|0|off)$/i.test(stringValue)) {
    return false
  }

  return defaultValue
}
